/**
 * @file useCheckinCalendar.js
 * @description 签到日历：当月记录、当日签到、补签、奖励特效
 * @module features/user/composables/useCheckinCalendar
 */

import { computed, ref, watch } from 'vue'
import { useUserStore } from '@/stores/user'
import {
  clockDoApi,
  clockMakeupApi,
  fetchClockRecordApi,
} from '@/features/user/api/userApi'

const WEEKDAYS = ['一', '二', '三', '四', '五', '六', '日']

/**
 * @param {{ showCalendar: boolean, continuousDays: number }} props
 * @param {(event: 'close' | 'checkin-success') => void} emit
 */
export function useCheckinCalendar(props, emit) {
  const now = new Date()
  const currentYear = ref(now.getFullYear())
  const currentMonth = ref(now.getMonth() + 1)
  const checkedDays = ref([])
  const loading = ref(false)
  const clickedDayEl = ref(null)
  const rewardEffect = ref(null)

  const calendarDays = computed(() => {
    const year = currentYear.value
    const month = currentMonth.value
    const firstDay = new Date(year, month - 1, 1)
    const lastDay = new Date(year, month, 0)
    const firstDayWeek = firstDay.getDay() === 0 ? 7 : firstDay.getDay()
    const daysInMonth = lastDay.getDate()
    const todayNow = new Date()
    const today = todayNow.getDate()
    const todayMonth = todayNow.getMonth() + 1
    const todayYear = todayNow.getFullYear()

    const days = []

    const prevMonth = month === 1 ? 12 : month - 1
    const prevYear = month === 1 ? year - 1 : year
    const prevMonthLastDay = new Date(prevYear, prevMonth, 0).getDate()
    for (let i = firstDayWeek - 1; i > 0; i--) {
      days.push({
        date: prevMonthLastDay - i + 1,
        otherMonth: true,
        isToday: false,
        checked: false,
        year: prevYear,
        month: prevMonth,
      })
    }

    for (let i = 1; i <= daysInMonth; i++) {
      const isToday =
        i === today && month === todayMonth && year === todayYear
      const checked = checkedDays.value.includes(i)
      days.push({
        date: i,
        otherMonth: false,
        isToday,
        checked,
        year,
        month,
      })
    }

    const totalCells = 42
    const remainingCells = totalCells - days.length
    for (let i = 1; i <= remainingCells; i++) {
      const nextMonth = month === 12 ? 1 : month + 1
      const nextYear = month === 12 ? year + 1 : year
      days.push({
        date: i,
        otherMonth: true,
        isToday: false,
        checked: false,
        year: nextYear,
        month: nextMonth,
      })
    }

    return days
  })

  async function loadCheckinRecord() {
    try {
      const response = await fetchClockRecordApi({
        clockYear: currentYear.value,
        clockMonth: currentMonth.value,
      })

      if (response.data.code === 200 || response.data.code === 0) {
        const data = response.data.data || {}
        checkedDays.value = data.clockArray || []
        if (data.continuousDays !== undefined && data.continuousDays !== null) {
          useUserStore().setContinuousDays(data.continuousDays)
        }
      } else {
        console.error('获取签到记录失败:', response.data.msg)
        checkedDays.value = []
      }
    } catch (error) {
      console.error('获取签到记录异常:', error)
      checkedDays.value = []
    }
  }

  function isPastDate(day) {
    const todayNow = new Date()
    const today = todayNow.getDate()
    const todayMonth = todayNow.getMonth() + 1
    const todayYear = todayNow.getFullYear()
    if (day.year < todayYear) return true
    if (day.year > todayYear) return false
    if (day.month < todayMonth) return true
    if (day.month > todayMonth) return false
    return day.date < today
  }

  function isToday(day) {
    const todayNow = new Date()
    return (
      day.date === todayNow.getDate() &&
      day.month === todayNow.getMonth() + 1 &&
      day.year === todayNow.getFullYear()
    )
  }

  function formatMakeupDate(year, month, date) {
    const m = String(month).padStart(2, '0')
    const d = String(date).padStart(2, '0')
    return `${year}-${m}-${d}`
  }

  function showRewardEffect(rewardGold) {
        if (rewardGold == null || rewardGold === undefined || !clickedDayEl.value) {
            return
    }
    if (rewardEffect.value) {
            return
    }
    const dayRect = clickedDayEl.value.getBoundingClientRect()
    rewardEffect.value = {
      rewardGold: Number(rewardGold),
      left: dayRect.left + dayRect.width / 2,
      top: dayRect.top - 28,
      _key: Date.now(),
    }
    const stayMs = 1500
    const leaveMs = 500
    const enterMs = 400
    setTimeout(() => {
      rewardEffect.value = null
          }, enterMs + stayMs + leaveMs)
  }

  async function doClock() {
        try {
      loading.value = true
      const response = await clockDoApi()

      if (response.data.code === 200 || response.data.code === 0) {
        const rewardGold = response.data.data?.rewardGold
                showRewardEffect(rewardGold)
        await loadCheckinRecord()
        emit('checkin-success')
      } else {
        console.error('签到失败:', response.data.msg)
        alert(response.data.msg || '签到失败')
      }
    } catch (error) {
      console.error('签到异常:', error)
      alert('签到失败，请稍后重试')
    } finally {
      loading.value = false
    }
  }

  async function doMakeup(day) {
        try {
      loading.value = true
      const makeupDate = formatMakeupDate(day.year, day.month, day.date)
      const response = await clockMakeupApi({ makeupDate })

      if (response.data.code === 200 || response.data.code === 0) {
        const rewardGold = response.data.data?.rewardGold
                showRewardEffect(rewardGold)
        if (day.otherMonth) {
          currentYear.value = day.year
          currentMonth.value = day.month
        }
        await loadCheckinRecord()
        emit('checkin-success')
      } else {
        console.error('补签失败:', response.data.msg)
        alert(response.data.msg || '补签失败')
      }
    } catch (error) {
      console.error('补签异常:', error)
      alert('补签失败，请稍后重试')
    } finally {
      loading.value = false
    }
  }

  async function handleDayClick(day, event) {
        if (loading.value) {
            return
    }
    if (event?.currentTarget) clickedDayEl.value = event.currentTarget

    if (!day.otherMonth && day.checked) {
            return
    }
    if (isToday(day)) {
            await doClock()
      return
    }
    if (isPastDate(day)) {
            await doMakeup(day)
    }
  }

  function prevMonth() {
    if (currentMonth.value === 1) {
      currentMonth.value = 12
      currentYear.value--
    } else {
      currentMonth.value--
    }
  }

  function nextMonth() {
    if (currentMonth.value === 12) {
      currentMonth.value = 1
      currentYear.value++
    } else {
      currentMonth.value++
    }
  }

  function canMakeup(day) {
    return isPastDate(day) && (!day.otherMonth ? !day.checked : true)
  }

  function closeCalendar() {
    emit('close')
  }

  watch(
    () => props.showCalendar,
    (newVal) => {
      if (newVal) {
        loadCheckinRecord()
      }
    }
  )

  watch(currentYear, () => {
    loadCheckinRecord()
  })

  watch(currentMonth, () => {
    loadCheckinRecord()
  })

  return {
    currentYear,
    currentMonth,
    weekdays: WEEKDAYS,
    calendarDays,
    rewardEffect,
    prevMonth,
    nextMonth,
    handleDayClick,
    canMakeup,
    closeCalendar,
  }
}
